import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ContactLeadMultiPicker } from "@/components/ContactLeadMultiPicker";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2 } from "lucide-react";

export interface PageAccessSettingsState {
  enabled: boolean;
  sharedLeadIds: string[];
  allowAccessRequests: boolean;
  autoGrantAccessRequests: boolean;
}

const ACCESS_TAB_IDS = ["settings", "requests"] as const;
type AccessTabId = (typeof ACCESS_TAB_IDS)[number];

function isValidAccessTab(tab: string | null): tab is AccessTabId {
  return !!tab && ACCESS_TAB_IDS.includes(tab as AccessTabId);
}

interface AccessRequest {
  _id: string;
  name: string;
  email: string;
  status: "pending" | "granted" | "denied";
  createdAt: string;
}

interface AccessRequestsTabProps {
  pageId?: string;
  access: PageAccessSettingsState;
  onAccessChange: (access: PageAccessSettingsState) => void;
  onSave?: () => void;
  isSaving?: boolean;
  hasUnsavedChanges?: boolean;
}

export function AccessRequestsTab({
  pageId,
  access,
  onAccessChange,
  onSave,
  isSaving,
  hasUnsavedChanges,
}: AccessRequestsTabProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const accessTabFromUrl = searchParams.get("accessTab");
  const activeAccessTab: AccessTabId = isValidAccessTab(accessTabFromUrl)
    ? accessTabFromUrl
    : "settings";

  const setActiveAccessTab = (tab: AccessTabId) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("accessTab", tab);
        return next;
      },
      { replace: true },
    );
  };

  const [requests, setRequests] = useState<AccessRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<"all" | "pending" | "granted" | "denied">(
    "pending",
  );
  const [actingId, setActingId] = useState<string | null>(null);

  const loadRequests = useCallback(async () => {
    if (!pageId) return;
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.set("status", statusFilter);
      const response = await fetch(
        `/api/pages/${pageId}/access-requests?${params.toString()}`,
      );
      if (!response.ok) {
        setRequests([]);
        return;
      }
      const data = await response.json();
      setRequests((data.requests || []) as AccessRequest[]);
    } catch (error) {
      console.error("Error loading access requests:", error);
      setRequests([]);
    } finally {
      setIsLoading(false);
    }
  }, [pageId, statusFilter]);

  useEffect(() => {
    if (activeAccessTab !== "requests") return;
    void loadRequests();
  }, [activeAccessTab, loadRequests]);

  const handleApprove = async (requestId: string) => {
    if (!pageId) return;
    setActingId(requestId);
    try {
      const response = await fetch(
        `/api/pages/${pageId}/access-requests/${requestId}/approve`,
        { method: "POST" },
      );
      if (!response.ok) {
        throw new Error("Failed to approve");
      }
      await loadRequests();
    } catch (error) {
      console.error("Approve access request error:", error);
      alert("Failed to approve request");
    } finally {
      setActingId(null);
    }
  };

  const handleDeny = async (requestId: string) => {
    if (!pageId) return;
    setActingId(requestId);
    try {
      const response = await fetch(
        `/api/pages/${pageId}/access-requests/${requestId}/deny`,
        { method: "POST" },
      );
      if (!response.ok) {
        throw new Error("Failed to deny");
      }
      await loadRequests();
    } catch (error) {
      console.error("Deny access request error:", error);
      alert("Failed to deny request");
    } finally {
      setActingId(null);
    }
  };

  return (
    <Tabs
      value={activeAccessTab}
      onValueChange={(value) => {
        if (isValidAccessTab(value)) {
          setActiveAccessTab(value);
        }
      }}
      className="w-full h-full flex flex-col"
    >
      <div className="p-4 border-b flex-shrink-0">
        <TabsList>
          <TabsTrigger value="settings">Settings</TabsTrigger>
          <TabsTrigger value="requests">Requests</TabsTrigger>
        </TabsList>
      </div>

      <TabsContent value="settings" className="flex-1 overflow-auto m-0 min-h-0 p-4 space-y-4">
          <div>
            <h3 className="text-sm font-medium">Access</h3>
            <p className="text-sm text-muted-foreground">
              Restrict who can view this page. Artist team members can always sign in with their email.
            </p>
          </div>
          <div className="flex items-center justify-between gap-4">
            <div className="space-y-0.5">
              <Label htmlFor="access-enabled">Restrict access</Label>
              <p className="text-xs text-muted-foreground">
                Visitors must verify their email with a one-time code
              </p>
            </div>
            <Switch
              id="access-enabled"
              checked={access.enabled}
              onCheckedChange={(enabled) =>
                onAccessChange({
                  ...access,
                  enabled,
                  ...(!enabled
                    ? { allowAccessRequests: false, autoGrantAccessRequests: false }
                    : {}),
                })
              }
            />
          </div>
          {access.enabled && (
            <>
              <ContactLeadMultiPicker
                value={access.sharedLeadIds}
                onChange={(sharedLeadIds) =>
                  onAccessChange({ ...access, sharedLeadIds })
                }
              />
              <div className="flex items-center justify-between gap-4">
                <div className="space-y-0.5">
                  <Label htmlFor="allow-access-requests">Allow access requests</Label>
                  <p className="text-xs text-muted-foreground">
                    Let visitors submit their name and email to request access
                  </p>
                </div>
                <Switch
                  id="allow-access-requests"
                  checked={access.allowAccessRequests}
                  onCheckedChange={(allowAccessRequests) =>
                    onAccessChange({
                      ...access,
                      allowAccessRequests,
                      autoGrantAccessRequests: allowAccessRequests
                        ? access.autoGrantAccessRequests
                        : false,
                    })
                  }
                />
              </div>
              {access.allowAccessRequests && (
                <div className="flex items-center justify-between gap-4">
                  <div className="space-y-0.5">
                    <Label htmlFor="auto-grant-access">Auto-grant access requests</Label>
                    <p className="text-xs text-muted-foreground">
                      Immediately grant access instead of requiring approval
                    </p>
                  </div>
                  <Switch
                    id="auto-grant-access"
                    checked={access.autoGrantAccessRequests}
                    onCheckedChange={(autoGrantAccessRequests) =>
                      onAccessChange({ ...access, autoGrantAccessRequests })
                    }
                  />
                </div>
              )}
            </>
          )}
          {onSave && (
            <Button
              type="button"
              onClick={() => onSave()}
              disabled={isSaving || !hasUnsavedChanges}
            >
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save"
              )}
            </Button>
          )}
      </TabsContent>

      <TabsContent value="requests" className="flex-1 overflow-auto m-0 min-h-0 p-4 space-y-4">
          {!pageId ? (
            <p className="text-sm text-muted-foreground">
              Save the page first to manage access requests.
            </p>
          ) : (
            <>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h3 className="text-sm font-medium">Access requests</h3>
                  <p className="text-sm text-muted-foreground">
                    Approve or deny people who requested access to this page.
                  </p>
                </div>
                <div className="flex gap-2">
                  {(["pending", "granted", "denied", "all"] as const).map((filter) => (
                    <Button
                      key={filter}
                      type="button"
                      size="sm"
                      variant={statusFilter === filter ? "default" : "outline"}
                      onClick={() => setStatusFilter(filter)}
                    >
                      {filter === "all" ? "All" : filter[0]!.toUpperCase() + filter.slice(1)}
                    </Button>
                  ))}
                </div>
              </div>

              {isLoading ? (
                <div className="flex items-center justify-center py-12 text-muted-foreground">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : requests.length === 0 ? (
                <p className="py-8 text-center text-sm text-muted-foreground">
                  No {statusFilter === "all" ? "" : `${statusFilter} `}access requests.
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Requested</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {requests.map((request) => (
                      <TableRow key={request._id}>
                        <TableCell>{request.name}</TableCell>
                        <TableCell>{request.email}</TableCell>
                        <TableCell className="capitalize">{request.status}</TableCell>
                        <TableCell>
                          {request.createdAt
                            ? new Date(request.createdAt).toLocaleString()
                            : "—"}
                        </TableCell>
                        <TableCell className="text-right">
                          {request.status === "pending" ? (
                            <div className="flex justify-end gap-2">
                              <Button
                                type="button"
                                size="sm"
                                onClick={() => handleApprove(request._id)}
                                disabled={actingId === request._id}
                              >
                                {actingId === request._id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  "Approve"
                                )}
                              </Button>
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={() => handleDeny(request._id)}
                                disabled={actingId === request._id}
                              >
                                Deny
                              </Button>
                            </div>
                          ) : request.status === "denied" ? (
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              onClick={() => handleApprove(request._id)}
                              disabled={actingId === request._id}
                            >
                              Approve
                            </Button>
                          ) : (
                            <span className="text-sm text-muted-foreground">—</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </>
          )}
      </TabsContent>
    </Tabs>
  );
}
